import { Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { ServiceEvent } from '../types';

const eventTypeLabels: Record<string, string> = {
  service: 'Service',
  repair: 'Repair',
  inspection: 'Inspection',
  manual_update: 'Manual Update',
  install: 'Installation',
};

const eventTypeColors: Record<
  string,
  'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error'
> = {
  service: 'primary',
  repair: 'warning',
  inspection: 'success',
  manual_update: 'secondary',
  install: 'default',
};

export function ServiceHistory() {
  const [events, setEvents] = useState<
    (ServiceEvent & { asset?: { serial_number: string | null; tag_id: string } })[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  async function fetchEvents() {
    try {
      const { data, error } = await supabase
        .from('asset_histories')
        .select(
          `
          id, asset_id, event_type, description, performed_by, event_at, metadata,
          asset:assets(serial_number, tag_id)
        `
        )
        .order('event_at', { ascending: false });

      if (error) throw error;
      setEvents(
        (data as (ServiceEvent & { asset?: { serial_number: string | null; tag_id: string } })[]) ||
          []
      );
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchEvents();
  }, []);

  const filteredEvents = events.filter(event => {
    const matchesSearch =
      event.description?.toLowerCase().includes(search.toLowerCase()) ||
      event.asset?.serial_number?.toLowerCase().includes(search.toLowerCase()) ||
      event.asset?.tag_id.toLowerCase().includes(search.toLowerCase());

    const matchesType = typeFilter === 'all' || event.event_type === typeFilter;

    return matchesSearch && matchesType;
  });

  return (
    <Box>
      <Typography variant="h4" component="h1" fontWeight={600} sx={{ mb: 3 }}>
        Service History
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                placeholder="Search events..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Event Type"
                  onChange={e => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="service">Service</MenuItem>
                  <MenuItem value="repair">Repair</MenuItem>
                  <MenuItem value="inspection">Inspection</MenuItem>
                  <MenuItem value="manual_update">Manual Update</MenuItem>
                  <MenuItem value="install">Installation</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 'auto', sm: 650 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Asset</TableCell>
                <TableCell>Type</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredEvents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No events found
                  </TableCell>
                </TableRow>
              ) : (
                filteredEvents
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map(event => (
                    <TableRow key={event.id} hover>
                      <TableCell sx={{ minWidth: { xs: 80, sm: 100 } }}>
                        <Typography variant="body2">
                          {new Date(event.event_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(event.event_at).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, maxWidth: 150 }}>
                        <Typography 
                          variant="body2" 
                          fontFamily="monospace"
                          sx={{
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {event.asset?.serial_number || event.asset?.tag_id || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: { xs: 100, sm: 120 } }}>
                        <Chip
                          label={eventTypeLabels[event.event_type] || event.event_type}
                          size="small"
                          color={eventTypeColors[event.event_type] || 'default'}
                        />
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            display: { xs: 'block', sm: 'none' }, 
                            mt: 0.5,
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {event.asset?.serial_number || event.asset?.tag_id || '-'}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            display: { xs: 'block', md: 'none' }, 
                            mt: 0.5,
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {event.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {event.description || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredEvents.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>
    </Box>
  );
}
